import React, { useCallback, useState } from 'react';

import { scaleLinear, scaleTime } from 'd3-scale';
import { max } from 'd3-array';

import flatten from 'lodash/flatten';

import {
  CURVE_TYPE,
  convertMousePointToAxisPoint,
  formatAxisDate,
  getClosestTimeSeriesPoint,
  getDistanceBetweenPointsByYOnly,
  roundedAxisValuesByRange,
} from '../d3-helpers';

import Area from './D3Area';
import ChartGrid from '../axis/D3ChartGrid';
import ClipSection from '../utility/D3ClipPath';
import Line from './D3Line';
import MouseoverDetection from '../utility/D3MouseoverDetection';
import MouseoverTooltip from '../utility/D3MouseoverTooltip';
import Points from '../points/D3Points';
import TimeRangeAxis from '../axis/D3TimeRangeAxis';
import { chartYAxisFormatter } from '../../../../utils/formatters';

const getAllYValues = (datasets = []) => flatten(datasets.map(({ data }) => data.map(({ y }) => y)))
const getAllDatapoints = (datasets = []) => flatten(datasets.map(dataset => dataset.data.map(item => ({ ...item, color: dataset.lineColor }))));

const DEFAULT_MARGINS = {
  top: 24,
  right: 32,
  bottom: 48,
  left: 56,
};

// Built assuming that time series data will be fed into it at a rate of around 10ms per point
export const D3LineChart = ({
  chartId,
  datasets,
  startDate,
  endDate,
  numberOfXTicks,
  numberOfYTicks,
  xAxisLabel,
  yAxisLabel,
  yTickFormat,
  yAxisPadding = 1.2,
  overrideMaxY = 0,
  areaOpacity = 0.5,
  xTickFormat = formatAxisDate,
  margins = DEFAULT_MARGINS,
  curveType = CURVE_TYPE.LINEAR,
  onPointClick = () => {},
  showPoints = false,
  showLine = true,
  showArea = true,
  showGrid = true,
  tooltipPointTolerance = 2,
  containerWidth = 800,
  containerHeight = 400,
}) => {
  const clipPathId = `${chartId}-clip-path`;
  const [tooltipPoint, setTooltipPoint] = useState(null);

  const width = containerWidth > 0 ? containerWidth - margins.left - margins.right : 0;
  const height = containerHeight > 0 ? containerHeight - margins.top - margins.bottom : 0;

  const allYValues = getAllYValues(datasets);
  const maxY = max(allYValues, val => val);

  // Consider basing padding and step sizing off of rounded square root / power of 2 values instead
  const paddedMaxY = ((Math.round(maxY / 10) * 10) || maxY) * yAxisPadding;

  const yTickValues = roundedAxisValuesByRange(0, paddedMaxY, numberOfYTicks);
  const displayedMaxY = max(yTickValues, val => val);

  const xScale = scaleTime()
    .domain([startDate, endDate])
    .range([0, width]);

  const yScale = scaleLinear()
    .domain([0, overrideMaxY || displayedMaxY])
    .range([height, 0])
    .nice();

  const getClosestPoint = useCallback((mousePosition) => {
    if (mousePosition) {
      const clickedPoint = convertMousePointToAxisPoint(xScale, yScale, mousePosition);

      if (clickedPoint?.x) {
        const allData = getAllDatapoints(datasets).map((item, index) => ({ ...item, index }));
        const closestPoint = getClosestTimeSeriesPoint(allData, clickedPoint);

        // ENG-5007 - Undefined values being found in chart as of recent update - this prevents crashes
        if (!closestPoint) {
          return null;
        }
        const distanceFromClosest = getDistanceBetweenPointsByYOnly(closestPoint, clickedPoint?.y);

        return distanceFromClosest <= tooltipPointTolerance ? closestPoint : null;
      }

      return null;
    }

    return null;
  }, [datasets, tooltipPointTolerance, xScale, yScale])

  const onMouseHover = useCallback((mousePosition) => {
    if (!mousePosition) {
      return null;
    }
    const clickedPoint = convertMousePointToAxisPoint(xScale, yScale, mousePosition);
    const closestPoint = getClosestPoint(mousePosition);
    if (!closestPoint) {
      setTooltipPoint(null);
    } else {
      const distanceFromClosest = getDistanceBetweenPointsByYOnly(closestPoint, clickedPoint?.y);

      if (distanceFromClosest <= tooltipPointTolerance) {
        setTooltipPoint(closestPoint)
      } else {
        setTooltipPoint(null);
      }
    }
  }, [getClosestPoint, tooltipPointTolerance, xScale, yScale])

  const handlePointClick = useCallback((mousePosition) => {
    const closestPoint = getClosestPoint(mousePosition);
    if (closestPoint) {
      onPointClick(closestPoint)
    }
  }, [getClosestPoint, onPointClick])

  return (
    <>
      <svg
        id={chartId}
        className="lineChartSvg"
        width={width + margins.left + margins.right}
        height={height + margins.top + margins.bottom}
      >
        <ClipSection
          id={clipPathId}
          x={0}
          y={0}
          height={height}
          width={width}
        />
        <g
          transform={`translate(${margins.left}, ${margins.top})`}
        >
          {
            showGrid && (
              <ChartGrid
                chartId={chartId}
                clipPathId={clipPathId}
                xScale={xScale}
                yScale={yScale}
                height={height}
                width={width}
                xTicks={numberOfXTicks}
                yTicks={numberOfYTicks}
              />
            )
          }
          <TimeRangeAxis
            chartId={chartId}
            clipPathId={clipPathId}
            xScale={xScale}
            yScale={yScale}
            height={height}
            width={width}
            margins={margins}
            xAxisLabel={xAxisLabel}
            yAxisLabel={yAxisLabel}
            xTickFormat={xTickFormat}
            yTickFormat={yTickFormat}
            xTicks={numberOfXTicks}
            yTicks={numberOfYTicks}
          />
          <g clipPath={`url(#${clipPathId})`}>
            {
              datasets?.length && showArea && (
                datasets.map(dataset => (
                  <Area
                    key={`${chartId}-${dataset.id}-area`}
                    id={`${chartId}-${dataset.id}-area`}
                    data={dataset.data}
                    fill={dataset.bgColor}
                    xScale={xScale}
                    yScale={yScale}
                    curve={curveType}
                    opacity={areaOpacity}
                  />
                ))
              )
            }
            {
              datasets?.length && showLine &&  (
                datasets.map(dataset => (
                  <Line
                    key={`${chartId}-${dataset.id}-line`}
                    id={`${chartId}-${dataset.id}-line`}
                    data={dataset.data}
                    color={dataset.lineColor}
                    xScale={xScale}
                    yScale={yScale}
                    curve={curveType}
                  />
                ))
              )
            }
            {
              datasets?.length && showPoints && (
                datasets.map(dataset => (
                  <Points
                    key={`${chartId}-${dataset.id}-points`}
                    id={`${chartId}-${dataset.id}-points`}
                    color={tooltipPoint?.color}
                    xScale={xScale}
                    yScale={yScale}
                    radius={8}
                    points={tooltipPoint ? [tooltipPoint] : []}
                  />
                ))
              )
            }
          </g>
          {
            showPoints && (
              <MouseoverDetection
                x={0}
                y={0}
                width={width}
                height={height}
                onClick={handlePointClick}
                onMouseHover={onMouseHover}
                hidePointer
              />
            )
          }
        </g>
      </svg>
      <MouseoverTooltip
        id={`${chartId}-tooltip`}
        xScale={xScale}
        yScale={yScale}
        tooltipPoint={tooltipPoint}
        formatLabel={(x, y) => `${xTickFormat(x)} - ${chartYAxisFormatter.format(y)}`}
      />
    </>
  )
};

export default D3LineChart;
